import {
  Checkbox,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
  Grid,
  TextField,
  IconButton,
  Button,
  Tooltip
} from '@mui/material';
import { Box } from '@mui/system';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CreateableMultiFilterNoContext from 'components/FiltersComponent/FilterTypes/MultiSelectCreateableFilterNoContext';
import SalesNavMultiSelectFromOptions from 'components/FiltersComponent/FilterTypes/SalesNavMultiSelectFromOptions';
import HeaderBasic from 'components/HeaderBasic';
import { useState, useEffect, useMemo } from 'react';
import { SalesNavigatorFilterType } from './types';
import { initialFilters } from './initialFilters';
import { buildSalesNavigatorUrl, MultiSelectItem, SalesNavigatorSearchCriteria } from './helper';

interface ManageApiKeysProps {}

const CreateSalesNavigatorLink = (_: ManageApiKeysProps) => {
  const [ai, setAi] = useState(false);
  const [aiDescription, setAiDescription] = useState('');

  const [filters, setFilters] = useState<SalesNavigatorFilterType[]>(initialFilters);

  // Additional form fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [connectionsOf, setConnectionsOf] = useState('');
  const [connectionLevel, setConnectionLevel] = useState<('F' | 'S' | 'O')[]>([]);
  const [followingYourCompany, setFollowingYourCompany] = useState(false);
  const [viewedYourProfileRecently, setViewedYourProfileRecently] = useState(false);
  const [pastColleague, setPastColleague] = useState(false);
  const [sharedExperiences, setSharedExperiences] = useState(false);
  const [changedJobs, setChangedJobs] = useState(false);
  const [postedOnLinkedIn, setPostedOnLinkedIn] = useState(false);

  const [generatedUrl, setGeneratedUrl] = useState('');

  const onFiltersChange = (data: SalesNavigatorFilterType) => {
    setFilters((prev) => [...prev.map((x) => (x.name === data.name ? data : x))]);
  };

  const getFilter = (path: string) => filters.find((f) => f.path === path)!;

  const renderFilter = (path: string) => {
    const filter = getFilter(path);
    if (filter.filterType === 'multiSelectCreatable') {
      return <CreateableMultiFilterNoContext filter={filter as any} onFilterChange={onFiltersChange as any} />;
    } else {
      return <SalesNavMultiSelectFromOptions filter={filter as any} onFilterChange={onFiltersChange as any} />;
    }
  };

  const toggleConnectionLevel = (level: 'F' | 'S' | 'O') => {
    setConnectionLevel((prev) => (prev.includes(level) ? prev.filter((l) => l !== level) : [...prev, level]));
  };

  // Helper to convert filter criteria to MultiSelectItem format
  const convertToMultiSelectItems = (filter: SalesNavigatorFilterType): MultiSelectItem[] => {
    return filter.criteria.map((criterion) => ({
      value: criterion.value,
      label: criterion.value,
      include: criterion.included
    }));
  };

  // Build the search criteria from current state
  const searchCriteria = useMemo((): SalesNavigatorSearchCriteria => {
    const getFilterByPath = (path: string) => filters.find((f) => f.path === path);

    return {
      firstName: firstName || undefined,
      lastName: lastName || undefined,
      connectionsOf: connectionsOf || undefined,
      connectionLevel: connectionLevel.length > 0 ? connectionLevel : undefined,
      followingYourCompany: followingYourCompany || undefined,
      viewedYourProfileRecently: viewedYourProfileRecently || undefined,
      pastColleague: pastColleague || undefined,
      sharedExperiences: sharedExperiences || undefined,
      changedJobs: changedJobs || undefined,
      postedOnLinkedIn: postedOnLinkedIn || undefined,

      currentCompany: getFilterByPath('currentCompany')
        ? convertToMultiSelectItems(getFilterByPath('currentCompany')!)
        : undefined,
      pastCompany: getFilterByPath('pastCompany')
        ? convertToMultiSelectItems(getFilterByPath('pastCompany')!)
        : undefined,
      companyType: getFilterByPath('companyType')
        ? convertToMultiSelectItems(getFilterByPath('companyType')!)
        : undefined,
      companyHeadcount: getFilterByPath('companyHeadcount')
        ? convertToMultiSelectItems(getFilterByPath('companyHeadcount')!)
        : undefined,
      companyHeadquartersLocation: getFilterByPath('companyHeadquartersLocation')
        ? convertToMultiSelectItems(getFilterByPath('companyHeadquartersLocation')!)
        : undefined,
      currentJobTitle: getFilterByPath('currentJobTitle')
        ? convertToMultiSelectItems(getFilterByPath('currentJobTitle')!)
        : undefined,
      pastJobTitle: getFilterByPath('pastJobTitle')
        ? convertToMultiSelectItems(getFilterByPath('pastJobTitle')!)
        : undefined,
      function: getFilterByPath('function') ? convertToMultiSelectItems(getFilterByPath('function')!) : undefined,
      seniorityLevel: getFilterByPath('seniorityLevel')
        ? convertToMultiSelectItems(getFilterByPath('seniorityLevel')!)
        : undefined,
      yearsAtCurrentCompany: getFilterByPath('yearsAtCurrentCompany')
        ? convertToMultiSelectItems(getFilterByPath('yearsAtCurrentCompany')!)
        : undefined,
      yearsInCurrentPosition: getFilterByPath('yearsInCurrentPosition')
        ? convertToMultiSelectItems(getFilterByPath('yearsInCurrentPosition')!)
        : undefined,
      geography: getFilterByPath('geography') ? convertToMultiSelectItems(getFilterByPath('geography')!) : undefined,
      industry: getFilterByPath('industry') ? convertToMultiSelectItems(getFilterByPath('industry')!) : undefined,
      profileLanguage: getFilterByPath('profileLanguage')
        ? convertToMultiSelectItems(getFilterByPath('profileLanguage')!)
        : undefined,
      yearsOfExperience: getFilterByPath('yearsOfExperience')
        ? convertToMultiSelectItems(getFilterByPath('yearsOfExperience')!)
        : undefined,
      groups: getFilterByPath('groups') ? convertToMultiSelectItems(getFilterByPath('groups')!) : undefined,
      school: getFilterByPath('school') ? convertToMultiSelectItems(getFilterByPath('school')!) : undefined
    };
  }, [
    filters,
    firstName,
    lastName,
    connectionsOf,
    connectionLevel,
    followingYourCompany,
    viewedYourProfileRecently,
    pastColleague,
    sharedExperiences,
    changedJobs,
    postedOnLinkedIn
  ]);

  // Generate URL whenever criteria changes
  useEffect(() => {
    const url = buildSalesNavigatorUrl(searchCriteria);
    setGeneratedUrl(url);
  }, [searchCriteria]);

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(generatedUrl);
  };

  return (
    <Stack spacing={2}>
      <HeaderBasic
        headerTitle="Create Sales Navigator link"
        subTitle="Generate a Sales Navigator link to automatically convert candidate data into lead records for your Hub"
        justifyContent="space-around"></HeaderBasic>

      {/* Generated URL Display */}
      <Box>
        <TextField
          fullWidth
          multiline
          rows={3}
          value={generatedUrl}
          label="Generated Sales Navigator URL"
          InputProps={{
            readOnly: true,
            endAdornment: (
              <IconButton onClick={handleCopyUrl} size="small" title="Copy URL">
                <ContentCopyIcon />
              </IconButton>
            )
          }}
          helperText="This URL is automatically generated based on your filter selections below"
        />
      </Box>

      <Box display="flex" justifyContent="end">
        <FormControlLabel
          control={<Switch checked={ai} onChange={() => setAi(!ai)} />}
          label="Use AI to generate link"
        />
      </Box>

      {ai ? (
        <Stack spacing={2}>
          <Box>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Describe your target in natural language
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={6}
              value={aiDescription}
              onChange={(e) => setAiDescription(e.target.value)}
              placeholder="Frontend developers in the United States at public companies, HQ in North America, 201–500 headcount..."
              label="AI Description"
            />
          </Box>
          <Box display="flex" justifyContent="flex-end">
            <Tooltip title="Still in development" arrow>
              <span>
                <Button variant="contained" disabled>
                  Generate
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Stack>
      ) : (
        <Stack spacing={3}>
          {/* Company Section */}
          <Stack spacing={1.5}>
            <Typography variant="h6" sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}>
              Company
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6} lg={4}>
                {renderFilter('currentCompany')}
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                {renderFilter('pastCompany')}
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                {renderFilter('companyType')}
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                {renderFilter('companyHeadcount')}
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                {renderFilter('companyHeadquartersLocation')}
              </Grid>
            </Grid>
          </Stack>

          {/* Role Section */}
          <Stack spacing={1.5}>
            <Typography variant="h6" sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}>
              Role
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6} lg={4}>
                {renderFilter('currentJobTitle')}
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                {renderFilter('pastJobTitle')}
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                {renderFilter('function')}
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                {renderFilter('seniorityLevel')}
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                {renderFilter('yearsAtCurrentCompany')}
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                {renderFilter('yearsInCurrentPosition')}
              </Grid>
            </Grid>
          </Stack>

          {/* Personal Section */}
          <Stack spacing={1.5}>
            <Typography variant="h6" sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}>
              Personal
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6} lg={4}>
                {renderFilter('geography')}
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                {renderFilter('industry')}
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="e.g., John"
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="e.g., Smith"
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                {renderFilter('profileLanguage')}
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                {renderFilter('yearsOfExperience')}
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                {renderFilter('groups')}
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                {renderFilter('school')}
              </Grid>
            </Grid>
          </Stack>

          {/* Buyer Intent Section */}
          <Stack spacing={1.5}>
            <Typography variant="h6" sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}>
              Buyer Intent
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6} lg={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={followingYourCompany}
                      onChange={(e) => setFollowingYourCompany(e.target.checked)}
                    />
                  }
                  label="Following Your Company"
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={viewedYourProfileRecently}
                      onChange={(e) => setViewedYourProfileRecently(e.target.checked)}
                    />
                  }
                  label="Viewed Your Profile Recently"
                />
              </Grid>
            </Grid>
          </Stack>

          {/* Best Path In Section */}
          <Stack spacing={1.5}>
            <Typography variant="h6" sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}>
              Best Path In
            </Typography>
            <Stack spacing={2}>
              <Stack spacing={1}>
                <Typography variant="subtitle2">Connection Level</Typography>
                <Box display="flex" gap={2} flexWrap="wrap">
                  <FormControlLabel
                    control={
                      <Checkbox checked={connectionLevel.includes('F')} onChange={() => toggleConnectionLevel('F')} />
                    }
                    label="1st Degree"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox checked={connectionLevel.includes('S')} onChange={() => toggleConnectionLevel('S')} />
                    }
                    label="2nd Degree"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox checked={connectionLevel.includes('O')} onChange={() => toggleConnectionLevel('O')} />
                    }
                    label="3rd Degree & Beyond"
                  />
                </Box>
              </Stack>

              <TextField
                fullWidth
                size="small"
                label="Connections Of"
                value={connectionsOf}
                onChange={(e) => setConnectionsOf(e.target.value)}
                placeholder="Enter LinkedIn profile URL"
              />

              <Grid container spacing={2}>
                <Grid item xs={12} md={6} lg={4}>
                  <FormControlLabel
                    control={<Checkbox checked={pastColleague} onChange={(e) => setPastColleague(e.target.checked)} />}
                    label="Past Colleague"
                  />
                </Grid>
                <Grid item xs={12} md={6} lg={4}>
                  <FormControlLabel
                    control={
                      <Checkbox checked={sharedExperiences} onChange={(e) => setSharedExperiences(e.target.checked)} />
                    }
                    label="Shared Experiences"
                  />
                </Grid>
              </Grid>
            </Stack>
          </Stack>

          {/* Recent Updates Section */}
          <Stack spacing={1.5}>
            <Typography variant="h6" sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}>
              Recent Updates
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6} lg={4}>
                <FormControlLabel
                  control={<Checkbox checked={changedJobs} onChange={(e) => setChangedJobs(e.target.checked)} />}
                  label="Changed Jobs in Past 90 Days"
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <FormControlLabel
                  control={
                    <Checkbox checked={postedOnLinkedIn} onChange={(e) => setPostedOnLinkedIn(e.target.checked)} />
                  }
                  label="Posted on LinkedIn in Past 30 Days"
                />
              </Grid>
            </Grid>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

export default CreateSalesNavigatorLink;
